// src/styles/taskStyles.js
import { alpha } from '@mui/material/styles';

export const dashboardContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    pb: 0,
};

export const viewToggleStyle = {
    mb: 2,
};

export const viewToggleButtonStyle = {
    textTransform: 'none',
    px: 2,
    py: 0.75,
};

// Unified single-row toolbar — sticky, paper bg, subtle border on scroll
export const toolbarStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    px: 2,
    py: 1,
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
};

// "Apple-quiet" toolbar button — borderless at rest, soft grey hover fill.
// Used by: Board picker, Filters, Sort/Due date, Grouped-By-Lead.
// Apply with variant="text" (not "outlined") so MUI does not inject its own
// outlined border on top of these styles.
export const toolbarButtonStyle = {
    textTransform: 'none',
    fontWeight: 400,
    fontSize: '0.82rem',
    color: 'text.secondary',
    borderRadius: '8px',
    height: 34,
    whiteSpace: 'nowrap',
    '&:hover': {
        bgcolor: (theme) => theme.palette.action.hover,
    },
};

// Active-filter overlay — spread after toolbarButtonStyle when the button's
// filter is currently active.  alpha(success.main, 0.12) fill + success.dark
// text.  Green is ONLY used here; idle controls stay neutral.
// Both values use theme callbacks so they resolve correctly in sx spreads.
export const toolbarButtonActiveStyle = {
    bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
    color: 'success.dark',
    '&:hover': {
        bgcolor: (theme) => alpha(theme.palette.success.main, 0.18),
    },
};

// Filter popover section label
export const filterSectionStyle = {
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'text.disabled',
    mb: 0.75,
    mt: 0.25,
    display: 'block',
};

// Filter chip within popover — larger tap target than default
export const filterChipStyle = {
    cursor: 'pointer',
    height: 30,
    fontSize: '0.78rem',
    borderRadius: '8px',
    '& .MuiChip-label': { px: 1.25 },
};

// Saved-view row pill
export const savedViewRowStyle = {
    cursor: 'pointer',
    height: 28,
    fontSize: '0.75rem',
    borderRadius: '14px',
    fontWeight: 400,
    '& .MuiChip-label': { px: 1.25 },
};

// Quick-Reply card-style CTA button — mirrors legacy Follow Up flow button card
// actionColor is passed inline (e.g. primary.main, '#25D366') via sx override
export const quickReplyButtonStyle = {
    width: '100%',
    height: 44,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    justifyContent: 'flex-start',
    px: 2,
    gap: 1.25,
    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
    },
    '&:active': {
        transform: 'translateY(0)',
    },
};

// Mini chip style for inline Priority / Status / Category chips below slideout title
export const miniChipStyle = {
    height: 24,
    fontSize: '0.68rem',
    fontWeight: 500,
    borderRadius: '5px',
    '& .MuiChip-label': { px: 1 },
};

// Header action button — text style, labeled, hover tint
export const headerActionButtonStyle = {
    textTransform: 'none',
    fontSize: '0.72rem',
    fontWeight: 500,
    minWidth: 0,
    height: 30,
    borderRadius: '6px',
    px: 0.75,
    gap: 0.4,
    lineHeight: 1,
    whiteSpace: 'nowrap',
};

// Thin vertical separator between header action groups
export const headerSeparatorStyle = {
    width: '1px',
    height: 16,
    bgcolor: 'divider',
    flexShrink: 0,
    mx: 0.5,
};

// Mini-chip row — 12px gap, wraps on overflow
export const miniChipRowStyle = {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 1.5,
    mb: 1.5,
};

// Section metadata label — matches V1 TaskSidebar "Reminder Notes" style (12/500/#888)
export const notesLabelStyle = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#888',
};

// Subtle accordion section for slideout progressive disclosure
export const accordionSectionStyle = {
    boxShadow: 'none !important',
    backgroundImage: 'none',
    '&.Mui-expanded': { boxShadow: 'none !important', margin: 0 },
    '&:before': { display: 'none' },
    '& .MuiAccordionSummary-root': {
        minHeight: 36,
        px: 0,
        '&.Mui-expanded': { minHeight: 36 },
    },
    '& .MuiAccordionSummary-content': {
        my: 0.5,
        '&.Mui-expanded': { my: 0.5 },
    },
    '& .MuiAccordionDetails-root': {
        px: 0,
        pt: 0.5,
        pb: 1.5,
    },
};
